import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { MembersPanel } from '@/features/team/MembersPanel';
import type { MemberBody, TenantBody } from '@/shared/api/generated/model';
import { sitesList } from '@/shared/api/generated/sites/sites';
import { usersListMembers } from '@/shared/api/generated/users/users';
import { ApiError } from '@/shared/api/http';
import { impersonate, useSession } from '@/shared/auth';
import { t } from '@/shared/i18n';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { toast } from '@/shared/ui/Toast';

export interface TenantMembersDialogProps {
  tenant?: TenantBody | undefined;
  onOpenChange: (open: boolean) => void;
}

/** Люди тенанта: роли, приглашения и вход от имени. */
export function TenantMembersDialog({ tenant, onOpenChange }: TenantMembersDialogProps) {
  return (
    <Dialog open={tenant !== undefined} onOpenChange={onOpenChange}>
      {tenant !== undefined && <Body tenant={tenant} onOpenChange={onOpenChange} />}
    </Dialog>
  );
}

function Body({
  tenant,
  onOpenChange,
}: {
  tenant: TenantBody;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const session = useSession();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['members', tenant.id],
    queryFn: async () => {
      const response = await usersListMembers(tenant.id);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос участников');
      }

      return response.data;
    },
  });

  const sites = useQuery({
    queryKey: ['sites', 'of-tenant', tenant.id],
    queryFn: async () => {
      const response = await sitesList({ tenant_id: tenant.id, per_page: 100 });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос сайтов');
      }

      return response.data.items ?? [];
    },
  });

  const enterAs = (member: MemberBody) => {
    impersonate(member.user_id)
      .then(() => {
        onOpenChange(false);
        void navigate({ to: '/' });
        toast.warning(t.team.impersonated(member.email), {
          description: t.team.impersonatedHint,
        });
      })
      .catch((cause: unknown) => {
        toast.error(t.team.impersonateFailed, {
          description: cause instanceof ApiError ? cause.detail : t.sites.unknownError,
        });
      });
  };

  const isSuperAdmin = session.status === 'authenticated' && session.user.is_super_admin;

  return (
    <DialogContent title={t.team.tenantTitle(tenant.name)} description={t.team.subtitle} size="lg">
      <MembersPanel
        scope={{ kind: 'tenant', tenantID: tenant.id, sites: sites.data ?? [] }}
        members={query.data?.members ?? []}
        invitations={query.data?.invitations ?? []}
        loading={query.isLoading}
        onChanged={() => {
          void queryClient.invalidateQueries({ queryKey: ['members', tenant.id] });
        }}
        onEnterAs={isSuperAdmin ? enterAs : undefined}
      />
    </DialogContent>
  );
}
